/** Explicit provenance of a task report, independent of its writer or assignee. */
export const TASK_REPORT_CHANNELS = [
  "pauta",
  "slack",
  "codex",
  "claude_code",
  "github",
  "api",
  "other",
] as const;

export type TaskReportChannel = (typeof TASK_REPORT_CHANNELS)[number];

export interface TaskReportOrigin {
  report_channel: TaskReportChannel | null;
  report_context: string | null;
  report_url: string | null;
}

type ParseResult =
  | { ok: true; value: TaskReportOrigin }
  | { ok: false; error: string };

const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;

export function isTaskReportChannel(value: unknown): value is TaskReportChannel {
  return typeof value === "string" &&
    (TASK_REPORT_CHANNELS as readonly string[]).includes(value);
}

/** A recorded permalink identifies a locator, never public visibility or a channel name. */
export function recordedSlackChannel(origin: {
  reportChannel?: TaskReportChannel | null; reportUrl?: string | null;
}): string | null {
  if (origin.reportChannel !== "slack") return null;
  const parsed = parseTaskReportOrigin({ report_channel: "slack", report_url: origin.reportUrl });
  if (!parsed.ok || !parsed.value.report_url) return null;
  const url = new URL(parsed.value.report_url);
  if (!/^[a-z0-9][a-z0-9-]*\.slack\.com$/.test(url.hostname) || url.port || url.hash) return null;
  const path = /^\/archives\/(C[A-Z0-9]{8,127})\/p\d{7,22}$/.exec(url.pathname);
  if (!path) return null;
  for (const [key, value] of url.searchParams) {
    if (key === "cid" && value === path[1]) continue;
    if (key === "thread_ts" && /^\d{1,16}\.\d{6}$/.test(value)) continue;
    return null;
  }
  return `${url.hostname}:${path[1]}`;
}

/**
 * Parse the three snake_case API fields. Omitted fields stay unknown; callers
 * must supply evidence, never infer an origin from a bot name or ticket prose.
 * This helper is safe to share with browser creation forms.
 */
export function parseTaskReportOrigin(input: unknown): ParseResult {
  if (input === null || input === undefined) {
    return { ok: true, value: { report_channel: null, report_context: null, report_url: null } };
  }
  if (typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, error: "Task report origin must be an object" };
  }

  const fields = input as Record<string, unknown>;
  const channel = fields.report_channel ?? null;
  if (channel !== null && !isTaskReportChannel(channel)) {
    return { ok: false, error: `report_channel must be one of: ${TASK_REPORT_CHANNELS.join(", ")}` };
  }

  const rawContext = fields.report_context;
  if (rawContext !== undefined && rawContext !== null && typeof rawContext !== "string") {
    return { ok: false, error: "report_context must be a string or null" };
  }
  if (typeof rawContext === "string" && CONTROL_CHARACTERS.test(rawContext)) {
    return { ok: false, error: "report_context cannot contain control characters" };
  }
  const context = typeof rawContext === "string" ? rawContext.trim() || null : null;
  if (context !== null && Array.from(context).length > 180) {
    return { ok: false, error: "report_context must be at most 180 characters" };
  }

  const rawUrl = fields.report_url;
  if (rawUrl !== undefined && rawUrl !== null && typeof rawUrl !== "string") {
    return { ok: false, error: "report_url must be a string or null" };
  }
  if (typeof rawUrl === "string" && CONTROL_CHARACTERS.test(rawUrl)) {
    return { ok: false, error: "report_url cannot contain control characters" };
  }
  let url = typeof rawUrl === "string" ? rawUrl.trim() || null : null;
  if (url !== null) {
    // Do not let URL's permissive normalization repair a malformed scheme,
    // whitespace, backslash authority or embedded credentials into a link.
    if (url.length > 2048 || !/^https:\/\/[^/?#]+/.test(url) || /[\s\\]/u.test(url)) {
      return { ok: false, error: "report_url must be an HTTPS URL of at most 2048 characters without credentials" };
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:" || !parsed.hostname || parsed.username || parsed.password ||
          url.slice("https://".length).split(/[/?#]/, 1)[0].includes("@")) {
        return { ok: false, error: "report_url must be an HTTPS URL without credentials" };
      }
      url = parsed.href;
      if (url.length > 2048) {
        return { ok: false, error: "report_url must be at most 2048 characters" };
      }
    } catch {
      return { ok: false, error: "report_url must be a valid HTTPS URL" };
    }
  }

  if (channel === null && (context !== null || url !== null)) {
    return { ok: false, error: "report_channel is required when report_context or report_url is supplied" };
  }
  return { ok: true, value: { report_channel: channel, report_context: context, report_url: url } };
}
